import React from "react";
import { connect } from "react-redux";

import Dialog from "../core/components/dialog/Dialog";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogOK from "../core/components/dialog/DialogOK";
import File from "../core/types/files/File";
import FileExplorer from "../core/components/fileExplorer/FileExplorer";
import Grid from "../core/components/grid/Grid";
import GridColumn, { GridColumnType } from "../core/components/grid/GridColumn";
import Import from "../core/components/import/Import";
import Info from "../core/components/info/Info";
import InputForm from "../core/components/inputform/InputForm";
import InputFormField from "../core/components/inputform/InputFormField";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import Panel from "../core/components/panels/Panel";
import SelectList from "../core/components/inputform/SelectList";
import ToolbarButton from "../core/components/toolbar/ToolbarButton";
import Wizard from "../core/components/wizard/Wizard";
import WizardStep from "../core/components/wizard/WizardStep";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { AppDispatch, IAppState } from "../core/state/Store";
import { DataSet } from "../core/types/data/DataSet";
import { DataTable } from "../core/types/data/DataTable";
import { FileExplorerTypes } from "../core/types/system/FileExplorer";
import {
    ImportLog,
    SkippedRecordCount,
    UpsertCount,
} from "../core/types/core/ImportLog";
import { Alignment, Side } from "../core/types/system/Layout";
import {
    addStudent,
    archiveStudent,
    importStudentsFromTD,
    initialiseStudentDetails,
    updateStudent,
} from "../core/state/core/Students";
import Student from "../core/types/core/Student";
import { PersonGenders } from "../core/types/enums/PersonGenders";
import {
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../core/types/data/SpreadsheetConfiguration";
import { initialiseYearLevelDetails } from "../core/state/core/YearLevel";
import YearLevel from "../core/types/core/YearLevel";
import Toggle from "../core/components/inputform/Toggle";
import Text from "../core/components/text/Text";
import { addAlert } from "../core/state/system/Alerts";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import RenderOrientations from "../core/types/enums/RenderOrientation";
import InputFormMandatory from "../core/components/inputform/InputFormMandatory";
import SortDialog from "../core/components/sorting/SortDialog";
import Utility from "../core/foundation/Utility";
import InputFieldTypes from "../core/types/enums/ImportFieldTypes";
import RecordsPanel from "../core/components/panels/RecordsPanel";
import EmailAddressEditor from "../core/components/emailaddresses/EmailAddressEditor";
import { ArrayKeys } from "../core/foundation/ArrayKeys";
import EmailAddress from "../core/types/core/EmailAddress";
import PhoneNumberEditor from "../core/components/phoneNumbers/PhoneNumberEditor";
import PhoneNumber from "../core/types/core/PhoneNumber";
import Address from "../core/types/core/Address";
import NumberTypes from "../core/types/enums/NumberTypes";
import AddressEditor from "../core/components/addresses/AddressEditor";
import ContactEditor from "../core/components/contact/ContactEditor";
import Contact from "../core/types/core/Contact";
import { getContacts } from "../core/state/core/Contact";

import template from "../resources/Student_Import_Template.xlsx";

import "./Students.scss";

interface RouteStudentsProps {
    archives: Student[];
    students: Student[];
    yearLevels: YearLevel[];

    setPageLoading: (loading: boolean) => void;
    initialiseStudentDetails: () => Promise<void>;
    initialiseYearLevelDetails: () => Promise<void>;
    addStudent: (details: Student[]) => Promise<void>;
    updateStudent: (details: Student[]) => Promise<void>;
    archiveStudent: (details: Student[]) => Promise<void>;
}

interface RouteStudentsState {
    archives: boolean;

    editOpen: boolean;
    editLoad: boolean;
    editTitle: string;
    editStep: number;

    sorting: Student[];
    sortOpen: boolean;
    sortLoad: boolean;

    importOpen: boolean;
    importLoad: boolean;
    importType: string;
    importStep: number;
    importWidth: number;
    importValid: boolean;

    selection: Student | null;
    selections: Student[];

    editing: Student | null;
}

class RouteStudents extends React.Component<
    RouteStudentsProps,
    RouteStudentsState
> {
    state: RouteStudentsState = {
        archives: false,

        editOpen: false,
        editLoad: false,
        editTitle: "",
        editStep: 0,

        sorting: [],
        sortOpen: false,
        sortLoad: false,

        importOpen: false,
        importLoad: false,
        importType: undefined,
        importStep: 0,
        importWidth: 400,
        importValid: false,

        selection: null,
        selections: [],

        editing: null,
    };

    static getDerivedStateFromProps(
        props: RouteStudentsProps,
        state: RouteStudentsState,
    ) {
        return {
            importWidth:
                state.importStep === 0
                    ? state.importType === "csv"
                        ? 1000
                        : state.importType === "tdf"
                        ? 580
                        : 580
                    : 580,
        };
    }

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);

            await Promise.all([
                this.props.initialiseStudentDetails(),
                this.props.initialiseYearLevelDetails(),
            ]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    add() {
        this.setState({
            editing: new Student({
                id: 0,
                code: "",
                firstName: "",
                middleName: "",
                lastName: "",
                gender: PersonGenders.Not_Specified,
                numbers: [],
                emails: [],
                addresses: [],
                contacts: [],
                yearLevel: "",
                rollClass: "",
                homeGroup: "",
                house: "",
                unitsToAssign: 0,
                order: 0,
            }),
            editOpen: true,
            editLoad: false,
            editTitle: "Add Student",
            editStep: 0,
        });
    }

    async edit() {
        if (!this.state.selection) return;

        try {
            this.props.setPageLoading(true);

            const temp = new Student(this.state.selection);
            for (const email of temp.emails)
                email.valid = Utility.validEmailAddress(email.address)
                    ? "check"
                    : "error";
            temp.contacts = await getContacts(temp.personID);
            this.setState({
                editing: temp,
                editOpen: true,
                editLoad: false,
                editTitle: "Edit Student",
                editStep: 0,
            });
        } finally {
            this.props.setPageLoading(false);
        }
    }

    updateChild<T>(
        type: ArrayKeys<Student, T>,
        original: T,
        updated: Partial<T>,
    ) {
        if (!this.state.editing) return;

        if (type === "emails") {
            let temp = updated as Partial<EmailAddress>;
            temp.valid = Utility.validEmailAddress(temp.address)
                ? "check"
                : "error";
        }

        this.setState({
            editing: new Student({
                ...this.state.editing,
                [type]: [
                    ...(this.state.editing[type] as Array<T>).filter(
                        (item: any) => item !== original,
                    ),
                    updated,
                ],
            }),
        });
    }

    deleteChild<T>(type: ArrayKeys<Student, T>, record: T) {
        if (!this.state.editing) return;

        this.setState({
            editing: new Student({
                ...this.state.editing,
                [type]: (this.state.editing[type] as Array<T>).filter(
                    (item: any) => item !== record,
                ),
            }),
        });
    }

    addEmail() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Student({
                ...this.state.editing,
                emails: [...this.state.editing.emails, new EmailAddress()],
            }),
        });
    }

    addNumber() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Student({
                ...this.state.editing,
                numbers: [
                    ...this.state.editing.numbers,
                    new PhoneNumber({
                        type: NumberTypes.Phone,
                    }),
                ],
            }),
        });
    }

    addAddress() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Student({
                ...this.state.editing,
                addresses: [...this.state.editing.addresses, new Address()],
            }),
        });
    }

    addContact() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Student({
                ...this.state.editing,
                contacts: [
                    ...(this.state.editing.contacts ?? []),
                    new Contact({
                        ownerId: this.state.editing.id,
                        firstName: "",
                        lastName: "",
                        gender: PersonGenders.Not_Specified,
                    }),
                ],
            }),
        });
    }

    cancelEdit() {
        this.setState({ editing: null, editOpen: false });
    }

    canSave(): boolean {
        if (!this.state.editing) return false;

        return !!(
            this.state.editing.code &&
            this.state.editing.firstName &&
            this.state.editing.lastName &&
            this.state.editing.gender &&
            this.state.editing.yearLevel
        );
    }

    async saveEdit() {
        if (!this.state.editing) return;

        try {
            this.setState({ editLoad: true });

            if (this.state.editing.id === 0)
                await this.props.addStudent([this.state.editing]);
            else await this.props.updateStudent([this.state.editing]);
        } finally {
            this.setState({ editing: null, editLoad: false, editOpen: false });
        }
    }

    async rowEdit(record: Student, changes: Student) {
        if (!this.state.archives) {
            this.props.updateStudent([new Student({ ...record, ...changes })]);
        }
    }

    async delete() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            icon: "delete_forever",
            styleColor: "red",
            title: "Archive Students",
            text: `Are you sure you want to archive the listed students?<br/><ul class="indented chevron"><li>${this.state.selections
                .map(item => item.display)
                .join("</li><li>")}</li></ul>`,
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);

                await this.props.archiveStudent(this.state.selections);

                this.setState({ selection: null, selections: [] });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    sort() {
        this.setState({
            sorting: this.props.students
                .map(student => new Student(student))
                .sort(Utility.getSorter([`order`])),
            sortOpen: true,
        });
    }

    async saveSort(sorted: Student[]) {
        try {
            this.setState({ sortLoad: true });

            for (const [newIndex, item] of sorted.entries())
                item.order = newIndex;

            await this.props.updateStudent(sorted);

            this.setState({ sortOpen: false });
        } finally {
            this.setState({ sortLoad: false });
        }
    }

    import() {
        this.setState({
            importOpen: true,
            importStep: 0,
            importType: undefined,
        });
    }

    importSetType(type: string) {
        this.setState({ importType: type, importStep: 0 });
    }

    importSetStep(step: number) {
        this.setState({ importStep: step });
    }

    importNextStep() {
        this.setState({ importStep: this.state.importStep + 1 });
    }

    importLastStep() {
        this.setState({ importStep: this.state.importStep - 1 });
    }

    importProceedChanged(valid: boolean) {
        this.setState({ importValid: valid });
    }

    importCancel() {
        this.setState({ importOpen: false });
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <Toggle
                        onLabel="Archives"
                        offLabel="Archives"
                        value={this.state.archives}
                        onChange={value =>
                            this.setState({ archives: value ?? false })
                        }
                    />
                    <ToolbarButton
                        icon="add"
                        text="Add"
                        tooltip="Add a new student"
                        onClick={this.add.bind(this)}
                    />
                    <ToolbarButton
                        icon="edit"
                        text="Edit"
                        tooltip={`Edit the selected student${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.edit.bind(this)}
                    />
                    <ToolbarButton
                        icon="archive"
                        text="Archive"
                        tooltip={`Archive the selected student${
                            this.state.selections.length > 1 ? "s" : ""
                        }`}
                        disabled={this.state.selections.length === 0}
                        onClick={this.delete.bind(this)}
                    />
                    <ToolbarButton
                        icon="filter_list"
                        text="Sort"
                        tooltip="Sort all students"
                        onClick={this.sort.bind(this)}
                    />
                    <ToolbarButton
                        icon="system_update_alt"
                        text="Import"
                        tooltip="Import students"
                        onClick={this.import.bind(this)}
                    />
                </MainToolbarContents>

                {(this.state.archives
                    ? this.props.archives?.length
                    : this.props.students?.length) > 0 ? (
                    <Grid<Student>
                        value={
                            this.state.archives
                                ? this.props.archives
                                : this.props.students
                        }
                        window={true}
                        collapsed={true}
                        fixedHeader={true}
                        keyField="id"
                        defaultSortField="order"
                        selectionChanged={(selection, selections) =>
                            this.setState({ selection, selections })
                        }
                        cellDoubleClicked={this.edit.bind(this)}
                        valueEdited={this.rowEdit.bind(this)}>
                        <GridColumn
                            header="No"
                            field="order"
                            selectable={true}
                            alignment={Alignment.Left}
                            width="40px"
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            header="First Name"
                            field="firstName"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            header="Family Name"
                            field="lastName"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                        <GridColumn
                            type={GridColumnType.Dropdown}
                            header="Year Level"
                            field="yearLevel"
                            selectable={true}
                            filterable={true}
                            options={this.props.yearLevels.map(item => ({
                                text: item.display,
                                value: item.code,
                                order: item.order,
                            }))}
                        />
                        <GridColumn
                            header="Roll Class"
                            field="rollClass"
                            selectable={true}
                            filterable={true}
                        />
                        <GridColumn
                            header="Home Group"
                            field="homeGroup"
                            selectable={true}
                            filterable={true}
                        />
                        <GridColumn
                            header="House"
                            field="house"
                            selectable={true}
                            filterable={true}
                        />
                        <GridColumn
                            type={GridColumnType.Dropdown}
                            header="Gender"
                            field="gender"
                            selectable={true}
                            filterable={true}
                            options={Utility.enumToOptions(PersonGenders)}
                        />
                        <GridColumn
                            type={GridColumnType.Checkbox}
                            header="User"
                            field="isUser"
                            selectable={true}
                            filterable={true}
                            editable={true}
                        />
                    </Grid>
                ) : (
                    <Text clearanceSide={Side.All}>
                        {this.state.archives
                            ? "No archived students found."
                            : "No active students found."}
                    </Text>
                )}

                <Dialog
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoad}
                    dialogIcon="edit"
                    dialogTitle={this.state.editTitle}
                    canResize={false}
                    width={800}>
                    <DialogContent>
                        <Panel>
                            <Panel>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Horizontal}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }
                                    columns={3}>
                                    <InputFormField
                                        label="First Name"
                                        field="firstName"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Middle Name"
                                        field="middleName"
                                    />
                                    <InputFormField
                                        label="Family Name"
                                        field="lastName"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Code"
                                        field="code"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="BOS Code"
                                        field="bosCode"
                                    />
                                    <InputFormField
                                        type={InputFieldTypes.Select}
                                        label="Gender"
                                        field="gender"
                                        mandatory={true}
                                        options={Utility.enumToOptions(
                                            PersonGenders,
                                        )}
                                    />
                                </InputForm>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Horizontal}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }
                                    columns={4}>
                                    <InputFormField
                                        label="Year Level"
                                        field="yearLevel"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Roll Class"
                                        field="rollClass"
                                    />
                                    <InputFormField
                                        label="Home Group"
                                        field="homeGroup"
                                    />
                                    <InputFormField
                                        label="House"
                                        field="house"
                                    />
                                    <InputFormField
                                        label="Units to Assign"
                                        field="unitsToAssign"
                                    />
                                </InputForm>
                            </Panel>

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Email Address"
                                clearanceSide={Side.Top}
                                canRemove={record =>
                                    !record.primary ||
                                    !this.state.editing.isUser
                                }
                                records={this.state.editing?.emails ?? []}
                                onAdd={this.addEmail.bind(this)}
                                onDelete={this.deleteChild.bind(this, "emails")}
                                onChange={this.updateChild.bind(this, "emails")}
                                editor={(record, _, update) => (
                                    <EmailAddressEditor
                                        value={record}
                                        valid={record.valid ?? ""}
                                        onChange={update}
                                    />
                                )}
                            />

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Phone Number"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.numbers ?? []}
                                onAdd={this.addNumber.bind(this)}
                                onDelete={this.deleteChild.bind(
                                    this,
                                    "numbers",
                                )}
                                onChange={this.updateChild.bind(
                                    this,
                                    "numbers",
                                )}
                                editor={(record, _, update) => (
                                    <PhoneNumberEditor
                                        value={record}
                                        onChange={update}
                                    />
                                )}
                            />

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Address"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.addresses ?? []}
                                onAdd={this.addAddress.bind(this)}
                                onDelete={this.deleteChild.bind(
                                    this,
                                    "addresses",
                                )}
                                onChange={this.updateChild.bind(
                                    this,
                                    "addresses",
                                )}
                                editor={(record, _, update) => (
                                    <AddressEditor
                                        value={record}
                                        onChange={update}
                                    />
                                )}
                            />

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Parent/Caregiver"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.contacts ?? []}
                                onAdd={this.addContact.bind(this)}
                                onDelete={this.deleteChild.bind(
                                    this,
                                    "contacts",
                                )}
                                onChange={this.updateChild.bind(
                                    this,
                                    "contacts",
                                )}
                                editor={(record, _, update) => (
                                    <ContactEditor
                                        value={record}
                                        editTooltip="Edit Parent/Caregiver"
                                        onChange={update}
                                    />
                                )}
                            />
                        </Panel>

                        <InputFormMandatory />
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            onClick={this.saveEdit.bind(this)}
                            disabled={!this.canSave()}
                        />
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.importOpen}
                    showLoading={this.state.importLoad}
                    dialogIcon="system_update_alt"
                    dialogTitle="Import"
                    canResize={false}
                    width={this.state.importWidth}>
                    <DialogContent>
                        <ConnectedImportWizard
                            importType={this.state.importType}
                            importStep={this.state.importStep}
                            setType={this.importSetType.bind(this)}
                            setStep={this.importSetStep.bind(this)}
                            validChanged={this.importProceedChanged.bind(this)}
                            setLoading={value =>
                                this.setState({ importLoad: value })
                            }
                        />
                    </DialogContent>
                    <DialogFooter>
                        {this.state.importStep !== 1 && (
                            <DialogCancel
                                text={
                                    this.state.importStep < 1
                                        ? "Cancel"
                                        : "Close"
                                }
                                onClick={this.importCancel.bind(this)}
                            />
                        )}
                        {this.state.importStep < 1 && (
                            <DialogOK
                                text="Next"
                                disabled={!this.state.importValid}
                                onClick={this.importNextStep.bind(this)}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                <SortDialog<Student>
                    keyField="id"
                    value={this.state.sorting}
                    showDialog={this.state.sortOpen}
                    showLoading={this.state.sortLoad}
                    onSave={this.saveSort.bind(this)}
                    onCancel={() => this.setState({ sortOpen: false })}>
                    <GridColumn
                        header="No"
                        field="order"
                        alignment={Alignment.Left}
                        width="40px"
                    />
                    <GridColumn
                        header="Code"
                        field="code"
                    />
                    <GridColumn
                        header="First Name"
                        field="firstName"
                    />
                    <GridColumn
                        header="Last Name"
                        field="lastName"
                    />
                    <GridColumn
                        type={GridColumnType.Dropdown}
                        header="Year Level"
                        field="yearLevel"
                        options={this.props.yearLevels.map(item => ({
                            text: item.display,
                            value: item.code,
                        }))}
                    />
                    <GridColumn
                        header="Roll Class"
                        field="rollClass"
                    />
                    <GridColumn
                        header="Home Group"
                        field="homeGroup"
                    />
                    <GridColumn
                        header="House"
                        field="house"
                    />
                    <GridColumn
                        type={GridColumnType.Dropdown}
                        header="Gender"
                        field="gender"
                        options={Utility.enumToOptions(PersonGenders)}
                    />
                    <GridColumn
                        type={GridColumnType.Checkbox}
                        header="User"
                        field="isUser"
                    />
                </SortDialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { student, yearLevel } = state.db;
    return {
        archives: student.archives,
        students: student.students,
        yearLevels: yearLevel.yearLevels,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseStudentDetails: () =>
            unwrappedDispatch(initialiseStudentDetails()),
        initialiseYearLevelDetails: () =>
            unwrappedDispatch(initialiseYearLevelDetails()),
        addStudent: (details: Student[]) =>
            unwrappedDispatch(addStudent(details)),
        updateStudent: (details: Student[]) =>
            unwrappedDispatch(updateStudent(details)),
        archiveStudent: (details: Student[]) =>
            unwrappedDispatch(archiveStudent(details)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteStudents);

interface ImportWizardProps {
    students: Student[];
    importType?: string;
    importStep: number;

    addStudent: (details: Student[]) => Promise<void>;
    updateStudent: (details: Student[]) => Promise<void>;
    setType?: (type: string) => void;
    setStep?: (step: number) => void;
    setLoading?: (loading: boolean) => void;
    validChanged?: (valid: boolean) => void;
}

interface ImportWizardState {
    importData?: DataSet;
    importFile?: File;
    importLogs?: ImportLog;
}

class ImportWizard extends React.Component<
    ImportWizardProps,
    ImportWizardState
> {
    state: ImportWizardState = {};

    private importTypes = [
        { value: "csv", text: "XLSX/CSV", order: 1 },
        { value: "tdf", text: "TD File", order: 2 },
    ];

    private importConfig: SpreadsheetConfiguration = {
        Name: "Students",
        Tables: [
            {
                Name: "Students",
                Columns: [
                    { PK: true, Field: "code", Name: "Code" },
                    { Field: "bosCode", Name: "BOS Code" },
                    { Field: "firstName", Name: "First Name" },
                    { Field: "middleName", Name: "Middle Name" },
                    { Field: "lastName", Name: "Family Name" },
                    { Field: "yearLevel", Name: "Year Level" },
                    { Field: "rollClass", Name: "Roll Class" },
                    { Field: "homeGroup", Name: "Home Group" },
                    { Field: "house", Name: "House" },
                    { Field: "gender", Name: "Gender" },
                    { Field: "Email Address", Name: "Email Address" },
                    {
                        Field: "Parent/Caregiver First Name",
                        Name: "Parent/Caregiver First Name",
                    },
                    {
                        Field: "Parent/Caregiver Last Name",
                        Name: "Parent/Caregiver Last Name",
                    },
                    {
                        Field: "Parent/Caregiver Email Address",
                        Name: "Parent/Caregiver Email Address",
                    },
                ],
            },
        ],
    };

    componentDidMount(): void {
        this.setState({
            importData: undefined,
        });
    }

    componentDidUpdate(
        prevProps: Readonly<ImportWizardProps>,
        prevState: Readonly<ImportWizardState>,
        snapshot?: any,
    ): void {
        if (prevProps.importType !== this.props.importType) {
            this.setState({
                importData: undefined,
                importFile: undefined,
            });

            this.props.validChanged?.(false);
        }

        if (prevProps.importStep !== this.props.importStep) {
            this.props.validChanged?.(this.importCanProceed());

            this.importProceed();
        } else if (prevState !== this.state) {
            this.props.validChanged?.(this.importCanProceed());
        }
    }

    importCanProceed(): boolean {
        switch (this.props.importStep) {
            case 0:
                switch (this.props.importType) {
                    case "csv":
                        return this.state.importData !== undefined;
                    case "tdf":
                        return this.state.importFile !== undefined;
                    default:
                        return false;
                }
            default:
                return false;
        }
    }

    importProceed() {
        switch (this.props.importStep) {
            case 1:
                switch (this.props.importType) {
                    case "csv":
                        this.doCsvImport();
                        break;
                    case "tdf":
                        this.doTDImport();
                        break;
                }
                break;
        }
    }

    async doCsvImport() {
        try {
            this.props.setStep?.(1);

            const adds = [];
            const upd = [];
            const logs: ImportLog = {
                upsertCounts: [],
                skippedItems: [],
            };

            for (const config of this.importConfig.Tables) {
                const table = this.state.importData[config.Name];
                if (!table) continue;

                this.processCsvImport(config, table, adds, upd, logs);
            }

            const waits: Promise<void>[] = [];
            if (adds.length > 0) waits.push(this.props.addStudent(adds));
            if (upd.length > 0) waits.push(this.props.updateStudent(upd));
            await Promise.all(waits);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    processCsvImport(
        type: SpreadsheetTableDefinition,
        data: DataTable,
        adds: Student[],
        upd: Student[],
        logs: ImportLog,
    ): void {
        for (const row of data) {
            const pk = type.Columns.find(c => c.PK)?.Field;
            if (!pk) continue;

            const pkVal = row[pk]?.toString().trim();
            if (!pkVal) continue;

            let student = this.props.students?.find(
                u => u[pk]?.toLowerCase() === pkVal.toLowerCase(),
            );

            if (!student) {
                student = new Student({
                    [pk]: pkVal,
                });

                adds.push(student);
            } else {
                student = new Student({ ...student });
                if (!upd.includes(student)) upd.push(student);
            }

            for (const column of type.Columns) {
                if (column.PK) continue;

                const value = row[column.Field]?.toString().trim();
                if (!value) continue;

                switch (column.Field) {
                    case "Parent/Caregiver First Name":
                    case "Parent/Caregiver Last Name":
                    case "Parent/Caregiver Email Address":
                        const contact =
                            student.contacts?.[0] ??
                            new Contact({ ownerId: student.id });

                        switch (column.Field) {
                            case "Parent/Caregiver First Name":
                                contact.firstName = value;
                                break;
                            case "Parent/Caregiver Last Name":
                                contact.lastName = value;
                                break;
                            case "Parent/Caregiver Email Address":
                                const email =
                                    contact.emails?.[0] ??
                                    new EmailAddress({ primary: true });
                                email.address = value;
                                contact.emails = contact.emails ?? [];
                                contact.emails[0] = email;
                                break;
                        }

                        student.contacts = student.contacts ?? [];
                        student.contacts[0] = contact;
                        break;
                    case "Email Address":
                        const email =
                            student.emails?.[0] ??
                            new EmailAddress({ primary: true });
                        email.address = value;
                        student.emails = student.emails ?? [];
                        student.emails[0] = email;
                        break;
                    default:
                        Reflect.set(
                            student,
                            column.Field as keyof Student,
                            value,
                        );
                        break;
                }
            }
        }

        logs.upsertCounts.push({
            name: type.Name,
            insertCount: adds.length,
            updateCount: upd.length,
            archiveCount: 0,
        });
    }

    async doTDImport() {
        try {
            this.props.setStep?.(1);

            const logs = await importStudentsFromTD(this.state.importFile.id);

            this.setState({
                importLogs: logs,
            });

            this.props.setStep?.(2);
        } catch {
            this.props.setStep?.(0);
        }
    }

    render() {
        return (
            <>
                <SelectList
                    tabs={true}
                    value={this.props.importType}
                    options={this.importTypes}
                    onChange={value =>
                        this.props.importStep === 0 &&
                        this.props.setType?.(value)
                    }
                />

                <Wizard
                    flow={true}
                    frameless={true}
                    transparent={true}
                    clearanceSide={Side.Top}
                    step={this.props.importStep}>
                    <WizardStep step={0}>
                        {(() => {
                            switch (this.props.importType) {
                                case "csv":
                                    return (
                                        <Import
                                            config={this.importConfig}
                                            template={template}
                                            templateName={template.stripReactName()}
                                            onReady={(_, data) =>
                                                this.setState({
                                                    importData: data,
                                                })
                                            }
                                        />
                                    );
                                case "tdf":
                                    return (
                                        <FileExplorer
                                            type={FileExplorerTypes.Timetables}
                                            value={this.state.importFile}
                                            onChange={item =>
                                                this.setState({
                                                    importFile: item,
                                                })
                                            }
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </WizardStep>
                    <WizardStep
                        step={1}
                        framed={true}
                        hiddenWhenOffscreen={true}>
                        <div className="buffer text-center">
                            <h2>We're currently importing your data</h2>
                        </div>
                        <div className="process-indicator">
                            <div className="cube cube1"></div>
                            <div className="cube cube2"></div>
                            <div className="cube cube3"></div>
                            <div className="cube cube4"></div>
                            <div className="cube cube5"></div>
                            <div className="cube cube6"></div>
                            <div className="cube cube7"></div>
                            <div className="cube cube8"></div>
                            <div className="cube cube9"></div>
                            <div className="cube cube10"></div>
                            <div className="cube cube11"></div>
                            <div className="document1"></div>
                            <div className="document2"></div>
                        </div>
                    </WizardStep>
                    <WizardStep
                        step={2}
                        hiddenWhenOffscreen={true}>
                        <Panel>
                            <Panel
                                title="Addition/Update Counts"
                                bufferSide={Side.All}>
                                {this.state?.importLogs?.upsertCounts?.length >
                                0 ? (
                                    <Grid<UpsertCount>
                                        value={
                                            this.state.importLogs
                                                ?.upsertCounts ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Added"
                                            field="insertCount"
                                        />
                                        <GridColumn
                                            header="Updated"
                                            field="updateCount"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>
                                        No records were added or updated.
                                    </Info>
                                )}
                            </Panel>

                            <Panel
                                title="Skipped Items"
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}>
                                {this.state?.importLogs?.skippedItems?.length >
                                0 ? (
                                    <Grid<SkippedRecordCount>
                                        value={
                                            this.state.importLogs
                                                ?.skippedItems ?? []
                                        }
                                        keyField="name"
                                        fullWidth={true}>
                                        <GridColumn
                                            header="Type"
                                            field="name"
                                        />
                                        <GridColumn
                                            header="Number"
                                            field="count"
                                        />
                                        <GridColumn
                                            header="Reason"
                                            field="reason"
                                        />
                                    </Grid>
                                ) : (
                                    <Info>No records were skipped.</Info>
                                )}
                            </Panel>
                        </Panel>
                    </WizardStep>
                </Wizard>
            </>
        );
    }
}

const mapStateToPropsWizard = (state: IAppState) => {
    const { student } = state.db;
    return {
        students: student.students,
    };
};

const mapDispatchToPropsWizard = (_dispatch: AppDispatch) => {
    return {
        addStudent: (details: Student[]) =>
            unwrappedDispatch(addStudent(details)),
        updateStudent: (details: Student[]) =>
            unwrappedDispatch(updateStudent(details)),
    };
};

const ConnectedImportWizard = connect(
    mapStateToPropsWizard,
    mapDispatchToPropsWizard,
)(ImportWizard);
